<script>
  import { createEventDispatcher, onDestroy, onMount } from 'svelte';
  import { Bell, Camera, LayoutDashboard, MonitorCog, Search, Shield, SmilePlus, SquareMenu, User } from 'lucide-svelte';
  import MenuSettingDetail from '@components/settings/MenuSettingDetail.svelte';
  import AccountDetail from '@components/settings/account/AccountDetail.svelte';
  import CameraDetail from '@components/settings/CameraDetail.svelte';
  import SystemDetail from '@components/settings/SystemDetail.svelte';
  import NotificationDetail from '@components/settings/NotificationDetail.svelte';
  import SecurityDetail from '@components/settings/SecurityDetail.svelte';
  import MessengerLayoutDetail from '@components/settings/MessengerLayoutDetail.svelte';
  import StickerMessengerDetail from '@components/settings/stickers/StickerMessengerDetail.svelte';
  import { loadAppSetting, saveAppSetting } from '$lib/app-settings.js';

  const dispatch = createEventDispatcher();
  const SETTINGS_PAGE_SETTING_KEY = 'settings.page';

  let activeTab = 'account';
  let searchQuery = '';
  let searchKeyword = '';
  let debounceTimer;
  let pageSettingTimer;
  let stickerSettingsRef;
  let modalState = { dirty: false, busy: false, mode: 'idle' };

  const tabs = [
    {
      id: 'account',
      label: 'Account',
      title: 'Account settings',
      description: 'Profile, work information, password, and PIN access.',
      icon: User
    },
    {
      id: 'menus',
      label: 'Menu',
      title: 'Menu management',
      description: 'Navigation entries, component routes, iframe links, and visibility.',
      icon: SquareMenu
    },
    {
      id: 'camera',
      label: 'Camera',
      title: 'Camera settings',
      description: 'Capture, recording, output, and ffmpeg defaults.',
      icon: Camera
    },
    {
      id: 'security',
      label: 'Security',
      title: 'Security settings',
      description: 'Password policy and sign-in controls.',
      icon: Shield
    },
    {
      id: 'notifications',
      label: 'Notifications',
      title: 'Notification settings',
      description: 'Delivery channels and alert preferences.',
      icon: Bell
    },
    {
      id: 'stickers',
      label: 'Sticker Messenger',
      title: 'Sticker Messenger',
      description: 'Sticker packs, OpenMoji import, and chat sticker publishing.',
      icon: SmilePlus
    },
    {
      id: 'messenger-layout',
      label: 'Layout Messenger',
      title: 'Thiết lập Layout Messenger',
      description: '',
      icon: LayoutDashboard
    },
    {
      id: 'system',
      label: 'System',
      title: 'System settings',
      description: 'Language, region, and operating preferences.',
      icon: MonitorCog
    }
  ];

  $: activeTabConfig = tabs.find((tab) => tab.id === activeTab) || tabs[0];
  $: filteredTabs = tabs.filter((tab) => tab.label.toLowerCase().includes(searchKeyword));

  onMount(() => {
    loadPageSetting();
  });

  onDestroy(() => {
    clearTimeout(debounceTimer);
    clearTimeout(pageSettingTimer);
  });

  function isValidTab(tabId) {
    return tabs.some((tab) => tab.id === tabId);
  }

  async function loadPageSetting() {
    try {
      const response = await loadAppSetting(SETTINGS_PAGE_SETTING_KEY);
      const savedTab = response?.effective?.activeTab;
      if (isValidTab(savedTab)) {
        activeTab = savedTab;
      }
    } catch (error) {
      console.warn('Cannot load settings page state:', error);
    }
  }

  function savePageSetting(tabId) {
    if (!isValidTab(tabId)) return;

    clearTimeout(pageSettingTimer);
    pageSettingTimer = setTimeout(() => {
      saveAppSetting(SETTINGS_PAGE_SETTING_KEY, { activeTab: tabId }, 'user').catch((error) => {
        console.warn('Cannot save settings page state:', error);
      });
    }, 350);
  }

  function updateSearch(event) {
    searchQuery = event.currentTarget.value;

    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      searchKeyword = searchQuery.trim().toLowerCase();
    }, 250);
  }

  function setTab(tab) {
    if (tab !== activeTab && activeTab === 'stickers' && stickerSettingsRef?.canNavigateAway?.() === false) {
      return;
    }

    activeTab = tab;
    searchQuery = '';
    searchKeyword = '';
    clearTimeout(debounceTimer);
    savePageSetting(tab);
  }

  function emitModalState(nextState = {}) {
    modalState = { ...modalState, ...nextState };
    dispatch('modalstate', modalState);
  }

  function handleStickerStateChange(event) {
    emitModalState(event.detail || {});
  }

  export async function confirmModal() {
    if (activeTab === 'stickers' && stickerSettingsRef?.commitChanges) {
      return await stickerSettingsRef.commitChanges();
    }
    return true;
  }

  export function canCloseModal() {
    if (activeTab === 'stickers' && stickerSettingsRef?.canCloseModal) {
      return stickerSettingsRef.canCloseModal();
    }
    return true;
  }

  $: if (activeTab !== 'stickers' && (modalState.dirty || modalState.busy || modalState.mode !== 'idle')) {
    emitModalState({ dirty: false, busy: false, mode: 'idle' });
  }
</script>

<div class="flex h-full min-h-0 flex-col overflow-hidden bg-slate-100 lg:flex-row">
  <aside class="hidden w-72 shrink-0 flex-col border-r border-slate-200 bg-white lg:flex">
    <div class="border-b border-slate-200 bg-slate-50 px-4 py-4">
      <div class="relative">
        <Search size={15} class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <input
          value={searchQuery}
          type="text"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck="false"
          placeholder="Search settings..."
          class="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-700 shadow-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
          on:input={updateSearch} />
      </div>
    </div>

    <nav class="flex-1 space-y-1 overflow-y-auto p-3">
      {#if filteredTabs.length}
        {#each filteredTabs as tab}
          <button
            type="button"
            class="flex w-full min-w-0 items-center gap-3 rounded-xl px-3 py-2.5 text-left text-sm transition {activeTab === tab.id
              ? 'bg-blue-50 text-blue-700 shadow-sm'
              : 'text-slate-700 hover:bg-slate-100'}"
            aria-current={activeTab === tab.id ? 'page' : undefined}
            on:click={() => setTab(tab.id)}>
            <span class="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg {activeTab === tab.id ? 'bg-white text-blue-700' : 'bg-slate-100 text-slate-500'}">
              <svelte:component this={tab.icon} size={17} />
            </span>
            <span class="min-w-0">
              <span class="block truncate font-medium">{tab.label}</span>
              {#if tab.description}
                <span class="block truncate text-xs {activeTab === tab.id ? 'text-blue-600' : 'text-slate-500'}">
                  {tab.description}
                </span>
              {/if}
            </span>
          </button>
        {/each}
      {:else}
        <div class="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-3 py-6 text-center text-sm text-slate-500">
          No settings found
        </div>
      {/if}
    </nav>
  </aside>

  <main class="flex min-h-0 min-w-0 flex-1 flex-col">
    <header class="shrink-0 border-b border-slate-200 bg-white">
      <div class="flex min-h-16 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-6">
        <div class="min-w-0">
          <p class="text-xs font-semibold uppercase text-slate-500">Settings</p>
          <h1 class="truncate text-xl font-semibold text-slate-900">{activeTabConfig.title}</h1>
          {#if activeTabConfig.description}
            <p class="mt-0.5 hidden text-sm text-slate-500 sm:block">{activeTabConfig.description}</p>
          {/if}
        </div>

        <div class="relative w-full sm:max-w-xs lg:hidden">
          <Search size={15} class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={searchQuery}
            type="text"
            autocomplete="off"
            autocorrect="off"
            autocapitalize="off"
            spellcheck="false"
            placeholder="Search settings..."
            class="h-10 w-full rounded-xl border border-slate-300 bg-white pl-9 pr-3 text-sm text-slate-700 shadow-sm outline-none placeholder:text-slate-400 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
            on:input={updateSearch} />
        </div>
      </div>

      <nav class="flex gap-2 overflow-x-auto border-t border-slate-200 px-4 py-2 lg:hidden">
        {#each filteredTabs as tab}
          <button
            type="button"
            class="inline-flex h-10 shrink-0 items-center gap-2 rounded-xl border px-3 text-sm font-medium transition {activeTab === tab.id
              ? 'border-blue-200 bg-blue-50 text-blue-700'
              : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}"
            aria-current={activeTab === tab.id ? 'page' : undefined}
            on:click={() => setTab(tab.id)}>
            <svelte:component this={tab.icon} size={16} />
            {tab.label}
          </button>
        {/each}
      </nav>
    </header>

    <div class="min-h-0 flex-1 overflow-y-auto">
      {#if activeTab === 'account'}
        <AccountDetail />
      {:else if activeTab === 'security'}
        <SecurityDetail />
      {:else if activeTab === 'notifications'}
        <NotificationDetail />
      {:else if activeTab === 'stickers'}
        <StickerMessengerDetail bind:this={stickerSettingsRef} on:statechange={handleStickerStateChange} />
      {:else if activeTab === 'messenger-layout'}
        <MessengerLayoutDetail />
      {:else if activeTab === 'camera'}
        <CameraDetail />
      {:else if activeTab === 'system'}
        <SystemDetail />
      {:else if activeTab === 'menus'}
        <MenuSettingDetail />
      {/if}
    </div>
  </main>
</div>
